import React, { Fragment, type RefObject, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Cloud, CloudIcon, CopyX, EllipsisVertical, Heart, type LucideIcon, Trash2, Users } from 'lucide-react';
import useResizeObserver from 'use-resize-observer';
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { NavigateToNodeMenuItem } from '@/components/menu-items/node-menu-items';
import { MenuProvider, dropdownPrimitives } from '@/components/menu-renderer';
import { RefreshViewButton } from '@/components/toolbars/refresh-view-button';
import { useActiveStaleMarks } from '@/hooks/websocket/useActiveStaleMarks';
import { useIsSearching } from '@/hooks/data/search/useIsSearching';
import { useDamNavigation } from '@/hooks/useDamNavigation';
import { Button } from '@/components/ui/button';
import { DAMNodeType } from '@/types/tree';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

export const originBreadcrumbMap = {
  browse: { path: '/browse', icon: CloudIcon },
  shared: { path: '/shared', icon: Users },
  duplicates: { path: '/duplicates', icon: CopyX },
  trash: { path: '/trash', icon: Trash2 },
};

type BreadcrumbItemData = { id: string; name: string; favorite?: boolean; onClick?: () => void };

type BaseProps = {
  readOnly?: boolean;
  rootIcon?: LucideIcon;
  onRootClick?: () => void;
  measureRef?: RefObject<HTMLElement | null>;
  reservedWidth?: number;
  actionMenu?: () => React.ReactNode;
  title?: string; // If set, show title mode instead of breadcrumb mode
  items?: Array<BreadcrumbItemData>;
  lastItem?: BreadcrumbItemData;
};

type FavoriteProps =
  | {
      enableFavorite: true; // Show heart button next to last item
      favorite: boolean; // Favorite state
      onFavoriteToggle: () => Promise<void> | void;
      favoriteDisabled?: boolean;
    }
  | {
      enableFavorite?: false | undefined;
      favorite?: never;
      onFavoriteToggle?: never;
      favoriteDisabled?: never;
    };

type BreadcrumbsProps = BaseProps & FavoriteProps;

export const Breadcrumbs = (props: BreadcrumbsProps) => {
  const isTitleMode = 'title' in props && props.title !== undefined;

  const { readOnly = false, rootIcon = Cloud, onRootClick, measureRef, reservedWidth = 0, actionMenu } = props;

  const items = !isTitleMode ? (props.items ?? []) : [];
  const lastItem = !isTitleMode ? props.lastItem : undefined;
  const enableFavorite = !isTitleMode && props.enableFavorite;

  const RootIcon = rootIcon;

  const [visibleItems, setVisibleItems] = useState<Array<BreadcrumbItemData>>(items);
  const [hiddenItems, setHiddenItems] = useState<Array<BreadcrumbItemData>>([]);
  const [lastItemMaxWidth, setLastItemMaxWidth] = useState<number | undefined>(undefined);

  const t = useTranslations('breadcrumb');
  const tMenu = useTranslations('menu.favorites');

  const { navigateToFolder, navigateToSearch } = useDamNavigation();
  const search = useIsSearching();
  // Same scoped source as RefreshViewButton, not the raw store: the truncation maths below
  // reserves the button's width, so it has to agree with whether the button actually renders.
  const hasStaleView = useActiveStaleMarks().length > 0;
  const { ref: containerRef, width: fallbackWidth = 0 } = useResizeObserver<HTMLDivElement>();
  const { width: externalWidth = 0 } = useResizeObserver<HTMLElement>({ ref: measureRef as any });

  const raw = measureRef ? externalWidth : fallbackWidth;
  const containerWidth = Math.max(0, Math.round(raw));
  const itemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const lastItemRef = useRef<HTMLLIElement | null>(null);
  const titleRef = useRef<HTMLSpanElement | null>(null);
  const allItemsHidden = useMemo(() => visibleItems.length === 0, [visibleItems]);

  const ITEM_GAP = 2; // <ol classname="gap-0.5">
  const ROOT_ICON_WIDTH = 24 + ITEM_GAP; // Root icon button width
  const SEPARATOR_WIDTH = 24 + ITEM_GAP; // <BreadcrumbSeparator /> width
  const ELLIPSIS_WIDTH = 24 + ITEM_GAP; // hidden-items dropdown button width
  const FAVORITES_WIDTH = 32 + ITEM_GAP; // Heart icon width with gap
  const ACTION_MENU_TRIGGER_WIDTH = 24 + ITEM_GAP; // Right-side action menu button width
  const REFRESH_VIEW_WIDTH = 28 + ITEM_GAP; // Reload-view button width (size-7)

  const sameById = (a: Array<BreadcrumbItemData>, b: Array<BreadcrumbItemData>) =>
    a.length === b.length && a.every((x, i) => x.id === b[i].id);

  useLayoutEffect(() => {
    if (isTitleMode) {
      return;
    }

    // Clear visible/hidden items when there are no ancestors (e.g., navigating to root folder)
    // This must run even if container not yet measured (containerWidth <= 0)
    if (!items.length) {
      setVisibleItems((prev) => (prev.length === 0 ? prev : []));
      setHiddenItems((prev) => (prev.length === 0 ? prev : []));
      return;
    }

    if (containerWidth <= 0) {
      return;
    }

    const lastW = lastItem ? lastItemRef.current?.scrollWidth : 0;
    const widths = items.map((it) => itemRefs.current[it.id]?.offsetWidth ?? 176);

    const breadcrumbsWidth =
      containerWidth -
      reservedWidth -
      ROOT_ICON_WIDTH -
      (enableFavorite && lastItem ? FAVORITES_WIDTH : 0) -
      (actionMenu?.length ? ACTION_MENU_TRIGGER_WIDTH : 0) -
      (hasStaleView ? REFRESH_VIEW_WIDTH : 0) -
      (lastW && lastItem ? SEPARATOR_WIDTH + lastW : 0);

    const pick = (withEllipsis: boolean) => {
      let remaining = breadcrumbsWidth - (withEllipsis ? SEPARATOR_WIDTH + ELLIPSIS_WIDTH : 0);
      let cut = items.length - 1;

      while (cut >= 0) {
        const cost = widths[cut] + SEPARATOR_WIDTH; // separator before each visible crumb
        if (remaining - cost > 0) {
          remaining -= cost;
          cut -= 1;
        } else {
          break;
        }
      }
      return { vis: items.slice(cut + 1), hid: items.slice(0, cut + 1) };
    };

    let res = pick(false);
    if (res.hid.length > 0) {
      res = pick(true);
    }

    setVisibleItems((prev) => (sameById(prev, res.vis) ? prev : res.vis));
    setHiddenItems((prev) => (sameById(prev, res.hid) ? prev : res.hid));
  }, [isTitleMode, containerWidth, items, lastItem, reservedWidth, actionMenu?.length, hasStaleView]);

  useLayoutEffect(() => {
    if (isTitleMode || containerWidth <= 0) {
      return;
    }

    const visibleCrumbsWidth = visibleItems.reduce((sum, it) => {
      const el = itemRefs.current[it.id];
      return sum + (el?.offsetWidth ?? 0) + SEPARATOR_WIDTH;
    }, 0);

    const breadcrumbsWidth =
      containerWidth -
      reservedWidth -
      ROOT_ICON_WIDTH -
      (enableFavorite && lastItem ? FAVORITES_WIDTH : 0) -
      (actionMenu ? ACTION_MENU_TRIGGER_WIDTH : 0) -
      (hasStaleView ? REFRESH_VIEW_WIDTH : 0) -
      (hiddenItems.length > 0 ? SEPARATOR_WIDTH + ELLIPSIS_WIDTH : 0) -
      visibleCrumbsWidth -
      (lastItem ? SEPARATOR_WIDTH : 0); // separator before last label

    // const next = Math.max(96, containerWidth - breadcrumbsWidth);
    setLastItemMaxWidth((prev) => (prev === breadcrumbsWidth ? prev : breadcrumbsWidth));
  }, [isTitleMode, containerWidth, reservedWidth, actionMenu, visibleItems, hiddenItems, lastItem, hasStaleView]);

  const setItemRef = (id: string) => (el: HTMLButtonElement | HTMLSpanElement | null) => {
    itemRefs.current[id] = el as any;
  };

  const renderItem = (item: BreadcrumbItemData) => (
    <BreadcrumbItem key={item.id}>
      <BreadcrumbLink asChild>
        {readOnly ? (
          <span
            ref={setItemRef(item.id)}
            className="block max-w-44 truncate overflow-hidden px-0 font-bold whitespace-nowrap"
          >
            {item.name}
          </span>
        ) : (
          <Button
            ref={setItemRef(item.id)}
            variant="main-toolbar"
            className="min-h-auto max-w-44 truncate rounded-md px-2! py-1 text-text-base"
            data-cy="breadcrumb-item-button"
            tooltip={item.name}
            onClick={() => {
              if (readOnly) {
                return;
              }
              if (item.onClick) {
                return item.onClick();
              }
              return search ? navigateToSearch(item.id, DAMNodeType.Folder) : navigateToFolder(item.id, item.name);
            }}
          >
            <span className="block truncate overflow-hidden font-bold whitespace-nowrap">{item.name}</span>
          </Button>
        )}
      </BreadcrumbLink>
    </BreadcrumbItem>
  );

  return (
    <Breadcrumb ref={measureRef ? undefined : containerRef} className="ml-2 flex min-w-0 grow items-center">
      <BreadcrumbList className="w-full min-w-0 flex-nowrap items-center gap-0.5 overflow-hidden">
        <BreadcrumbItem>
          {onRootClick ? (
            <BreadcrumbLink>
              <Button
                variant="main-toolbar"
                size="main-toolbar"
                onClick={onRootClick}
                data-cy="breadcrumb-root-button"
                tooltip={t('root')}
                icon={RootIcon}
              />
            </BreadcrumbLink>
          ) : (
            <div className="flex icon-7 items-center justify-center">
              <RootIcon className="icon-6 text-icon-base" />
            </div>
          )}
        </BreadcrumbItem>

        {isTitleMode && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="min-w-0">
              <div className="flex min-w-0 items-center">
                <BreadcrumbPage ref={titleRef} title={props.title} className="truncate font-bold">
                  {props.title}
                </BreadcrumbPage>
              </div>
            </BreadcrumbItem>
          </>
        )}

        {/* 2. Ellipsis dropdown if needed */}
        {!isTitleMode && hiddenItems.length > 0 && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <BreadcrumbEllipsis aria-label={t('showMore')} />
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" sideOffset={10}>
                  <MenuProvider primitives={dropdownPrimitives}>
                    {hiddenItems.map((item) => (
                      <NavigateToNodeMenuItem
                        key={item.id}
                        node={item}
                        disabled={readOnly}
                        onSelect={({ id, name }) => {
                          if (readOnly) {
                            return;
                          }
                          if (item.onClick) {
                            return item.onClick();
                          }
                          return search ? navigateToSearch(id, DAMNodeType.Folder) : navigateToFolder(id, name);
                        }}
                      />
                    ))}
                  </MenuProvider>
                </DropdownMenuContent>
              </DropdownMenu>
            </BreadcrumbItem>
          </>
        )}

        {/* 3. Visible middle items */}
        {!isTitleMode &&
          [...visibleItems].map((it) => (
            <Fragment key={it.id}>
              <BreadcrumbSeparator />
              {renderItem(it)}
            </Fragment>
          ))}

        {/* 4. Last item with icons, takes remaining space */}
        {!isTitleMode && lastItem && (
          <>
            <BreadcrumbSeparator />
            <BreadcrumbItem ref={lastItemRef} className="flex gap-x-2">
              <BreadcrumbPage
                title={lastItem.name}
                className={cn('block whitespace-nowrap min-h-auto rounded-md px-2! py-1', {
                  'line-clamp-1 truncate': allItemsHidden,
                })}
                style={allItemsHidden ? { maxWidth: lastItemMaxWidth } : {}}
              >
                {lastItem.name}
              </BreadcrumbPage>
              {props.enableFavorite && props.onFavoriteToggle && lastItem && (
                <Button
                  variant="main-toolbar"
                  size="main-toolbar"
                  onClick={props.onFavoriteToggle}
                  aria-pressed={props.favorite}
                  aria-label={props.favorite ? tMenu('remove') : tMenu('add')}
                  data-cy={'favorite-toggle-action'}
                  tooltip={props.favorite ? tMenu('remove') : tMenu('add')}
                  icon={Heart}
                  iconClassName={cn(
                    'icon-6',
                    props.favorite ? 'fill-status-failure stroke-status-failure!' : 'stroke-icon-base',
                  )}
                />
              )}
            </BreadcrumbItem>
          </>
        )}
        {actionMenu && (
          <BreadcrumbItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="main-toolbar" size="main-toolbar" data-cy="breadcrumb-action-menu-button">
                  <EllipsisVertical className="size-6" strokeWidth={1.5} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" sideOffset={10}>
                <MenuProvider primitives={dropdownPrimitives}>{actionMenu()}</MenuProvider>
              </DropdownMenuContent>
            </DropdownMenu>
          </BreadcrumbItem>
        )}
        {hasStaleView && (
          <BreadcrumbItem>
            <RefreshViewButton />
          </BreadcrumbItem>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
};
